import {
  IconButton,
  InputAdornment,
  makeStyles,
  TextField,
  TextFieldProps,
} from "@material-ui/core"
import clsx from "clsx"
import { FC, useState } from "react"
import { colors } from "../../../utils/theme"
import EyeOffIcon from "../EyeOffIcon"
import EyeOnIcon from "../EyeOnIcon"

const useStyles = makeStyles({
  labelRoot: {
    color: `${colors.neutral.asphalt} !important`,
  },
  labelError: {
    color: `${colors.secondary.crimson} !important`,
  },
  labelFocused: {},
  inputRoot: {
    borderWidth: "1px !important",
    "&:hover $notchedOutline": {
      borderColor: colors.primary.periwinkle,
    },
    "&$disabled $notchedOutline": {
      borderColor: colors.neutral.clay,
    },
    "&:hover": {
      backgroundColor: colors.primary.cloud,
    },
    "&:active": {
      color: colors.neutral.denim,
    },
    "&:focus": {
      color: colors.neutral.denim,
    },
  },
  inputFocused: {},
  inputDisabled: {},
  notchedOutline: {},
  disabled: {
    opacity: 0.5,
    borderWidth: 1,
    borderColor: colors.neutral.clay,
    backgroundColor: colors.neutral.white,
    color: colors.neutral.denim,
    "&:placeholder": {
      color: colors.neutral.denim,
    },
  },
  iconButton: {
    "&:hover": {
      backgroundColor: "transparent",
      boxShadow: "none",
      outline: "none",
    },
    "&:focus": {
      outline: "none",
    },
  },
  labelShrink: {
    shrink: true,
  },
})

type Props = {
  hovered?: boolean
  enableShowPassword?: boolean
  shrink?: boolean
} & TextFieldProps

const Input: FC<Props> = ({
  focused,
  hovered,
  enableShowPassword,
  helperText,
  ...props
}) => {
  const classes = useStyles()
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [focus, setFocus] = useState<boolean>(false)
  return (
    <TextField
      {...props}
      helperText={helperText === " " ? undefined : helperText}
      onFocus={(event) => {
        setFocus(true)
        props?.onFocus?.(event)
      }}
      onBlur={(event) => {
        setFocus(false)
        props?.onBlur?.(event)
      }}
      onMouseOver={props.onMouseOver}
      onMouseOut={props.onMouseOut}
      variant="outlined"
      className={clsx(
        props.disabled ? classes.disabled : undefined,
        props.className
      )}
      InputLabelProps={{
        classes: {
          root: classes.labelRoot,
          focused: classes.labelFocused,
          error: classes.labelError,
        },
        ...props.InputLabelProps,
      }}
      InputProps={{
        classes: {
          root: classes.inputRoot,
          focused: classes.inputFocused,
          disabled: classes.disabled,
          notchedOutline: classes.notchedOutline,
        },
        endAdornment:
          enableShowPassword && (focus || focused) ? (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword((prev) => !prev)}
                onMouseDown={(event) => event.preventDefault()}
                edge="end"
                className={classes.iconButton}
              >
                {showPassword ? <EyeOnIcon /> : <EyeOffIcon />}
              </IconButton>
            </InputAdornment>
          ) : undefined,
      }}
      FormHelperTextProps={{
        ...props.FormHelperTextProps,
      }}
      type={
        enableShowPassword && (focus || focused)
          ? showPassword
            ? "text"
            : "password"
          : props.type
      }
    />
  )
}

export default Input
